import { Fragment } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./RealtimeAnalytics.module.css";

/* ── Mock Data ── */

// prettier-ignore
const HR_DATA_RAW = [
  // Warmup: resting → light activity (80-105)
  80, 82, 79, 84, 86, 83, 88, 91, 87, 93, 96, 92, 98, 101, 97, 104,
  // Building intensity (105-140)
  108, 105, 112, 109, 115, 118, 114, 122, 126, 121, 130, 134, 128, 137, 141, 136,
  // High intensity (140-175)
  143, 148, 144, 152, 149, 156, 160, 155, 163, 167, 162, 170, 174, 168, 172, 176,
  // Peak zone (175-198)
  180, 177, 185, 190, 186, 194, 192, 197, 193, 198, 195, 191, 188, 184,
  // Cooldown (175-145)
  178, 173, 176, 168, 164, 170, 162, 156, 160, 152, 148, 155, 150, 145,
];
const HR_DATA = HR_DATA_RAW;

const SHOT_DATA = [
  // Zone 1-2 (Paint) - early session, low HR -> high FG%
  { time: 5, made: true, zone: "restricted-area", hr: 88 },
  { time: 8, made: true, zone: "paint", hr: 93 },
  { time: 10, made: true, zone: "restricted-area", hr: 98 },
  // Zone 3 (Mid) - warming up, moderate HR -> decent FG%
  { time: 16, made: true, zone: "mid-range", hr: 108 },
  { time: 18, made: false, zone: "mid-range", hr: 115 },
  { time: 21, made: true, zone: "mid-range", hr: 122 },
  // Zone 4 (3PT) - HR climbing, some still go in
  { time: 26, made: true, zone: "three-point", hr: 134 },
  { time: 28, made: false, zone: "three-point", hr: 141 },
  { time: 31, made: true, zone: "three-point", hr: 148 },
  { time: 33, made: false, zone: "three-point", hr: 156 },
  // Zone 5 (Deep) - HR high, mostly misses
  { time: 37, made: false, zone: "deep", hr: 167 },
  { time: 39, made: false, zone: "deep", hr: 174 },
  { time: 41, made: false, zone: "deep", hr: 185 },
  // Back to closer shots at peak HR - still miss
  { time: 44, made: false, zone: "three-point", hr: 194 },
  { time: 46, made: true, zone: "restricted-area", hr: 197 },
  { time: 47, made: false, zone: "three-point", hr: 198 },
  // Cooldown
  { time: 51, made: true, zone: "mid-range", hr: 176 },
  { time: 54, made: true, zone: "paint", hr: 160 },
  { time: 57, made: true, zone: "restricted-area", hr: 148 },
];

const HR_ZONES = [
  { min: 60, max: 100, color: "rgba(52, 199, 89, 0.08)", label: "Zone 1" },
  { min: 100, max: 120, color: "rgba(0, 113, 227, 0.08)", label: "Zone 2" },
  { min: 120, max: 140, color: "rgba(255, 204, 0, 0.08)", label: "Zone 3" },
  { min: 140, max: 160, color: "rgba(255, 149, 0, 0.10)", label: "Zone 4" },
  { min: 160, max: 200, color: "rgba(255, 59, 48, 0.12)", label: "Zone 5" },
];

const ZONE_FG_DATA = [
  { fg: 72, color: "#ff3b30" },
  { fg: 55, color: "#ff9500" },
  { fg: 41, color: "#34c759" },
  { fg: 28, color: "#0071e3" },
];

const MONTHLY_DATA = [
  { fg: 45, sessions: 12 },
  { fg: 52, sessions: 15 },
  { fg: 61, sessions: 18 },
];

/* ── SVG Helpers ── */

const SVG_WIDTH = 720;
const SVG_HEIGHT = 320;
const PADDING_LEFT = 48;
const PADDING_RIGHT = 24;
const PADDING_TOP = 24;
const PADDING_BOTTOM = 48;
const GRAPH_WIDTH = SVG_WIDTH - PADDING_LEFT - PADDING_RIGHT;
const GRAPH_HEIGHT = SVG_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

const HR_MIN = 60;
const HR_MAX = 200;
const HR_THRESHOLD = 160;

function hrToY(hr: number): number {
  const ratio = (hr - HR_MIN) / (HR_MAX - HR_MIN);
  return PADDING_TOP + GRAPH_HEIGHT * (1 - ratio);
}

function timeToX(index: number, total: number): number {
  return PADDING_LEFT + (index / (total - 1)) * GRAPH_WIDTH;
}

/**
 * Convert data points to a smooth cubic Bezier path using Catmull-Rom → Bezier conversion.
 * This produces the natural-looking HR monitor curve with realistic variability.
 */
function buildHrPath(): string {
  const pts = HR_DATA.map((hr, i) => ({
    x: timeToX(i, HR_DATA.length),
    y: hrToY(hr),
  }));

  if (pts.length < 2) return "";

  const d: string[] = [`M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`];
  const tension = 0.3; // lower = smoother, higher = more jagged

  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];

    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;

    d.push(
      `C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`,
    );
  }

  return d.join(" ");
}

function buildHrAreaPath(): string {
  const linePath = buildHrPath();
  const lastX = timeToX(HR_DATA.length - 1, HR_DATA.length).toFixed(1);
  const firstX = timeToX(0, HR_DATA.length).toFixed(1);
  const bottomY = (PADDING_TOP + GRAPH_HEIGHT).toFixed(1);
  return `${linePath} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
}

const HR_PATH = buildHrPath();
const HR_AREA_PATH = buildHrAreaPath();

const HR_GRID_LINES = [80, 100, 120, 140, 160, 180, 200];

function shotTimeToX(time: number): number {
  const maxTime = HR_DATA.length - 1;
  const ratio = time / maxTime;
  return PADDING_LEFT + ratio * GRAPH_WIDTH;
}

/** Map shot to the HR curve's actual data point for accurate dot placement */
function shotCurveY(time: number): number {
  const idx = Math.round(Math.min(Math.max(time, 0), HR_DATA.length - 1));
  return hrToY(HR_DATA[idx]);
}

/* ── Component ── */

export function RealtimeAnalytics() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const ZONE_FG = ZONE_FG_DATA.map((d, i) => ({
    ...d,
    group: t.ai.analyticsZoneGroups[i],
  }));

  const MONTHLY = MONTHLY_DATA.map((d, i) => ({
    ...d,
    month: t.ai.analyticsMonths[i],
  }));

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const hrLine = el.querySelector(`.${styles.hrLine}`) as SVGPathElement | null;
      const hrArea = el.querySelector(`.${styles.hrArea}`) as SVGPathElement | null;
      const shotDots = el.querySelectorAll("[data-shot-dot]");
      const zoneFills = el.querySelectorAll(`.${styles.zoneBarFill}`);
      const monthCards = el.querySelectorAll(`.${styles.monthCard}`);
      const monthArrows = el.querySelectorAll(`.${styles.monthArrow}`);
      const monthlyInsight = el.querySelector(`.${styles.monthlyInsight}`);
      const insightBubbles = el.querySelectorAll(`.${styles.insightBubble}`);

      if (isMobile) {
        /* Mobile: simple staggered fade-in */
        const panels = el.querySelectorAll(
          `.${styles.timelinePanel}, .${styles.zonePanel}, .${styles.monthlySection}, .${styles.insightsSection}`,
        );

        gsap.set(panels, { y: 30, opacity: 0 });
        gsap.to(panels, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.15,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
        return;
      }

      /* Desktop: pinned scroll with scrub */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          pin: true,
          scrub: 1,
          start: "top top",
          end: "+=300%",
        },
      });

      /* Phase 1: HR line draws left to right */
      if (hrLine) {
        const pathLength = hrLine.getTotalLength();
        gsap.set(hrLine, {
          strokeDasharray: pathLength,
          strokeDashoffset: pathLength,
        });
        tl.to(hrLine, {
          strokeDashoffset: 0,
          duration: 3,
          ease: "none",
        });
      }

      if (hrArea) {
        gsap.set(hrArea, { opacity: 0 });
        tl.to(
          hrArea,
          {
            opacity: 0.15,
            duration: 1,
            ease: "power2.out",
          },
          "<1",
        );
      }

      /* Phase 2: Shot dots appear one by one */
      if (shotDots.length > 0) {
        gsap.set(shotDots, { scale: 0, opacity: 0 });
        tl.to(
          shotDots,
          {
            scale: 1,
            opacity: 1,
            duration: 0.3,
            stagger: 0.15,
            ease: "back.out(2)",
          },
          "-=1.5",
        );
      }

      /* Phase 3: Zone FG% bars grow */
      if (zoneFills.length > 0) {
        gsap.set(zoneFills, { scaleX: 0 });
        tl.to(zoneFills, {
          scaleX: 1,
          duration: 1.2,
          stagger: 0.2,
          ease: "power3.out",
        });
      }

      /* Phase 4: Monthly progression cards slide in */
      if (monthCards.length > 0) {
        gsap.set(monthCards, { y: 30, opacity: 0 });
        gsap.set(monthArrows, { opacity: 0 });

        tl.to(monthCards, {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.3,
          ease: "power3.out",
        });

        tl.to(
          monthArrows,
          {
            opacity: 1,
            duration: 0.4,
            stagger: 0.3,
            ease: "power2.out",
          },
          "<0.15",
        );
      }

      if (monthlyInsight) {
        gsap.set(monthlyInsight, { opacity: 0, y: 10 });
        tl.to(monthlyInsight, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
        });
      }

      /* Phase 5: AI insight bubbles appear */
      if (insightBubbles.length > 0) {
        gsap.set(insightBubbles, { y: 20, opacity: 0 });
        tl.to(insightBubbles, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.2,
          ease: "power3.out",
        });
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t.ai.analyticsTitle}</h2>
        <p className={styles.subtitle}>{t.ai.analyticsSubtitle}</p>

        {/* Top Grid: Timeline + Zone FG% */}
        <div className={styles.topGrid}>
          {/* Timeline Graph */}
          <div className={styles.timelinePanel}>
            <div className={styles.timelineLabel}>{t.ai.analyticsTimelineLabel}</div>
            <div className={styles.svgWrap}>
              <svg
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Heart rate timeline with shot results showing correlation between elevated heart rate and missed shots"
              >
                <defs>
                  <linearGradient id="raHrGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ff3b30" />
                    <stop offset="60%" stopColor="#ff6b6b" />
                    <stop offset="100%" stopColor="#ff3b30" />
                  </linearGradient>
                  <linearGradient id="raHrAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3b30" />
                    <stop offset="100%" stopColor="transparent" />
                  </linearGradient>
                </defs>

                {/* HR zone color bands */}
                {HR_ZONES.map((zone) => {
                  const y = hrToY(zone.max);
                  const h = hrToY(zone.min) - y;
                  return (
                    <g key={zone.label}>
                      <rect
                        x={PADDING_LEFT}
                        y={y}
                        width={GRAPH_WIDTH}
                        height={h}
                        fill={zone.color}
                      />
                      <text
                        x={SVG_WIDTH - PADDING_RIGHT - 4}
                        y={y + h / 2 + 3}
                        textAnchor="end"
                        className={styles.hrZoneLabel}
                      >
                        {zone.label}
                      </text>
                    </g>
                  );
                })}

                {/* Grid lines */}
                {HR_GRID_LINES.map((hr) => (
                  <g key={hr}>
                    <line
                      x1={PADDING_LEFT}
                      y1={hrToY(hr)}
                      x2={SVG_WIDTH - PADDING_RIGHT}
                      y2={hrToY(hr)}
                      className={styles.gridLine}
                    />
                    <text
                      x={PADDING_LEFT - 6}
                      y={hrToY(hr) + 3}
                      textAnchor="end"
                      className={styles.hrLabel}
                    >
                      {hr}
                    </text>
                  </g>
                ))}

                {/* HR threshold line at 160 */}
                <line
                  x1={PADDING_LEFT}
                  y1={hrToY(HR_THRESHOLD)}
                  x2={SVG_WIDTH - PADDING_RIGHT}
                  y2={hrToY(HR_THRESHOLD)}
                  className={styles.hrThresholdLine}
                />
                <text
                  x={SVG_WIDTH - PADDING_RIGHT + 4}
                  y={hrToY(HR_THRESHOLD) + 3}
                  className={styles.hrThresholdLabel}
                >
                  {t.ai.analyticsBpmThreshold}
                </text>

                {/* Time axis labels */}
                {[0, 15, 30, 45, 59].map((i) => (
                  <text
                    key={i}
                    x={timeToX(i, HR_DATA.length)}
                    y={SVG_HEIGHT - 8}
                    textAnchor="middle"
                    className={styles.timeAxisLabel}
                  >
                    {`${Math.round((i / 59) * 30)}:00`}
                  </text>
                ))}

                {/* HR area fill */}
                <path d={HR_AREA_PATH} className={styles.hrArea} />

                {/* HR line */}
                <path d={HR_PATH} className={styles.hrLine} />

                {/* Shot dots */}
                {SHOT_DATA.map((shot) => {
                  const cx = shotTimeToX(shot.time);
                  const cy = shotCurveY(shot.time);
                  return (
                    <circle
                      key={`shot-${shot.time}`}
                      data-shot-dot=""
                      cx={cx}
                      cy={cy}
                      r={7}
                      className={shot.made ? styles.shotDotMake : styles.shotDotMiss}
                    />
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Zone FG% Breakdown */}
          <div className={styles.zonePanel}>
            <div className={styles.zonePanelTitle}>{t.ai.analyticsZoneTitle}</div>
            <div className={styles.zoneRows}>
              {ZONE_FG.map((zone) => (
                <div key={zone.group} className={styles.zoneRow}>
                  <div className={styles.zoneRowHeader}>
                    <span className={styles.zoneRowLabel}>{zone.group}</span>
                    <span className={styles.zoneRowValue} style={{ color: zone.color }}>
                      {zone.fg}%
                    </span>
                  </div>
                  <div className={styles.zoneBarTrack}>
                    <div
                      className={styles.zoneBarFill}
                      style={{
                        width: `${zone.fg}%`,
                        background: `linear-gradient(90deg, ${zone.color}, ${zone.color}88)`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 3-Month Progression */}
        <div className={styles.monthlySection}>
          <div className={styles.monthlyTitle}>{t.ai.analyticsMonthlyTitle}</div>
          <div className={styles.monthlyGrid}>
            {MONTHLY.map((m, i) => {
              const fgClass =
                i === 0 ? styles.monthFgFirst : i === 1 ? styles.monthFgMiddle : styles.monthFgLast;

              return (
                <Fragment key={m.month}>
                  {i > 0 && <div className={styles.monthArrow}>&#8594;</div>}
                  <div className={styles.monthCard}>
                    <div className={styles.monthLabel}>{m.month}</div>
                    <div className={`${styles.monthFg} ${fgClass}`}>{m.fg}%</div>
                    <div className={styles.monthSessions}>
                      {m.sessions} {t.ai.analyticsSessions}
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
          <p className={styles.monthlyInsight}>{t.ai.analyticsMonthlyInsight}</p>
        </div>

        {/* AI Insights */}
        <div className={styles.insightsSection}>
          {t.ai.analyticsInsights.map((insight) => (
            <div key={insight} className={styles.insightBubble}>
              <div className={styles.insightIcon}>AI</div>
              <p className={styles.insightText}>{insight}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
